import React, { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GhostButton } from '@ui/GhostButton';
import type { CandidateRow } from '@shared/models/element-import.model';
import type { IElement } from '@shared/models/project.model';
import { SaveMappingDialog } from './SaveMappingDialog';
import { useCandidateSaveMapping } from './use-candidate-save-mapping.hook';
import { saveMappingActionLabel } from './save-mapping-action.helpers';

type CandidateSaveMappingButtonProps = {
  row: CandidateRow;
  importedElement?: IElement;
};

export const CandidateSaveMappingButton: React.FC<
  CandidateSaveMappingButtonProps
> = ({ row, importedElement }) => {
  const { classes } = useStyles();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const {
    canSaveMapping,
    saveMappingAction,
    saveMappingTargetCount,
    matchingRowCount,
    handleSaveMapping,
  } = useCandidateSaveMapping({ row, importedElement });

  // A mapping is keyed by resource code, so rows without one (e.g. a Solibri
  // export with an empty `Type Mark`) can't be remembered. Block + explain.
  const hasResourceCode = Boolean(row.resourceCode);
  const action = saveMappingAction ?? 'save';
  const showDisabledNoCode = !hasResourceCode && canSaveMapping;
  if (!showDisabledNoCode && saveMappingAction === undefined) return null;

  const button = (
    <GhostButton
      disabled={!canSaveMapping || !hasResourceCode}
      onClick={() => setSaveDialogOpen(true)}
    >
      {saveMappingActionLabel(action, saveMappingTargetCount)}
    </GhostButton>
  );

  return (
    <Box
      className={classes.hoverAction}
      data-candidate-row-hover-action
      onClick={(event) => event.stopPropagation()}
    >
      {hasResourceCode ? (
        button
      ) : (
        <Tooltip title="This row has no resource code, so its mapping can't be saved.">
          <span>{button}</span>
        </Tooltip>
      )}
      {saveDialogOpen && hasResourceCode && saveMappingAction && (
        <SaveMappingDialog
          onClose={() => setSaveDialogOpen(false)}
          matchingRowCount={matchingRowCount}
          targetCount={saveMappingTargetCount}
          action={saveMappingAction}
          onSave={handleSaveMapping}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  hoverAction: {
    alignItems: 'center',
    display: 'flex',
    padding: 4,
    pointerEvents: 'none',
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    visibility: 'hidden',
    zIndex: 1,
  },
}));
